import React, { FunctionComponent, memo } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useParams } from "react-router-dom";
import AnimatedLoadingButton from "../../../../../components/AnimatedLoadingButton";
import TooltipWrapper from "../../../../../enhancers/TooltipWrapper";
import {
  useDroppedFilesStaticAnalysis,
  useGetRLSandboxResourcesStatus
} from "../../../hooks";
import { DROPPED_FILES_ACTIONS } from "../consts";
import { getDownloadDroppedFilesDisabledMessage } from "../utils";

type DroppedFilesStaticAnalysisButtonProps = {
  analysisId: string;
  isMergedReport: boolean;
  isStoredReport: boolean;
  isPrivate: boolean;
  isNetworkReport?: boolean;
};

const DroppedFilesStaticAnalysisButton: FunctionComponent<
  DroppedFilesStaticAnalysisButtonProps
> = ({
  analysisId,
  isMergedReport,
  isStoredReport,
  isPrivate,
  isNetworkReport
}) => {
  const {
    hash: summaryDetailsHash,
    sha1: cloudDetailsHash,
    sourceId
  } = useParams();
  const hash = summaryDetailsHash || cloudDetailsHash;
  const { sendToStaticAnalysis, isLoading } = useDroppedFilesStaticAnalysis(
    hash || "",
    analysisId
  );
  const { data } = useGetRLSandboxResourcesStatus(
    hash || "",
    analysisId,
    isNetworkReport,
    {
      enabled: Boolean(analysisId)
    }
  );

  const handleClick = () => {
    sendToStaticAnalysis();
  };

  const loadingContent = "Analyzing";
  const nonLoadingContent = isMergedReport
    ? DROPPED_FILES_ACTIONS.SEND_LATEST_TO_STATIC_ANALYSIS_SHORT
    : DROPPED_FILES_ACTIONS.SEND_TO_STATIC_ANALYSIS_SHORT;

  const content = isLoading ? loadingContent : nonLoadingContent;

  const tooltipText = getDownloadDroppedFilesDisabledMessage(
    isStoredReport,
    data?.has_dropped_files,
    isPrivate,
    isLoading,
    sourceId
  );

  return (
    <TooltipWrapper
      condition={
        isLoading ||
        (data && !data.has_dropped_files) ||
        !sourceId ||
        isStoredReport ||
        isPrivate
      }
      wrapper={(children: React.ReactNode) => (
        <OverlayTrigger
          overlay={
            <Tooltip id="animated-button-tooltip">{tooltipText}</Tooltip>
          }
          placement="top"
        >
          <div>{children}</div>
        </OverlayTrigger>
      )}
    >
      {
        <AnimatedLoadingButton
          disabled={
            !data?.has_dropped_files || !sourceId || isStoredReport || isPrivate
          }
          isLoading={isLoading}
          size="sm"
          variant="outline-dark"
          onClick={handleClick}
        >
          {content}
        </AnimatedLoadingButton>
      }
    </TooltipWrapper>
  );
};

export default memo(DroppedFilesStaticAnalysisButton);
